import { useRouteParams } from "src/hooks/useRouteParams";
import { SwapProductInCatalogFormInput } from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/inCatalog/swapProductInCatalogWorkflow";
import { WorkflowActionArgs } from "src/routes/projects/change-events/guided/workflows/types";
import { ChangeEventParams } from "src/routes/routesDef";
import { GuidedChangeEventStep } from "../../../utils";
import { useSwapProduct_SaveChangeEventMutation } from "./useSwapProductInCatalogActions.generated";

/**
 * Defines the actions for the swap product in catalog workflow.
 *
 * For SelectProductToReplaceStep:
 * - Saves the staged swaps when leaving the "Select Product to Replace" step, as one exhaustive
 *   list: whatever the form holds is what the change event ends up with. A swap the user dropped is
 *   simply not in the list anymore, and the backend deletes it along with the line items it manages.
 * - Folds the saved change event back into the form, so the next advance doesn't re-send everything.
 */
export function useSwapProductInCatalogActions(args: WorkflowActionArgs<SwapProductInCatalogFormInput>) {
  const { form } = args;
  const { changeEventId } = useRouteParams<ChangeEventParams>();
  const [saveChangeEvent] = useSwapProduct_SaveChangeEventMutation();

  return {
    [GuidedChangeEventStep.SelectProductToReplace]: async () => {
      if (!form.valid) return false;
      if (!form.dirty) return true;

      const materialVariantSwaps = form.swaps.value;

      const result = await saveChangeEvent({ variables: { input: { id: changeEventId, materialVariantSwaps } } });
      const savedChangeEvent = result.data?.saveChangeEvent.changeEvent;
      if (!savedChangeEvent) return false;

      // Fold the saved change event back into the form: `update` re-runs the workflow's `mapToForm`
      // so the rows pick up their new swap ids and the form goes clean. Without it, the next advance
      // would re-send rows the backend already deleted and re-created.
      form.update(savedChangeEvent);
      return true;
    },
  };
}
